'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { SecretInput } from '@/components/form/secret-input';
import { useToast } from '@/components/ui/use-toast';

import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from '@/lib/money';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

const AVAILABLE_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];

const CURRENCY_OPTIONS = AVAILABLE_CURRENCIES.map((currency) => ({
  label: currency,
  value: currency,
}));

interface RouteSenseEditPageProps {
  processor: ProcessorDetails;
}

const RouteSenseEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id);

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6 py-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <RouteSenseEditPage processor={data} />;
};

const RouteSenseEditPage = ({ processor }: RouteSenseEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();

  const [validationError, setValidationError] = useState<string | null>(null);
  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    baseCurrency: processor.baseCurrency || 'USD',
    supportedCurrencies: processor.supportedCurrencies || [],
    monthlyProcessingCapacity: minorUnitsToMajorUnits(
      processor.monthlyProcessingCapacity || 0,
      processor.baseCurrency || 'USD',
    ),
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    securityKey: (processor.options as any)?.securityKey || '',
    descriptor: (processor.options as any)?.descriptor?.descriptor || '',
    mcc: (processor.options as any)?.descriptor?.mcc || '',
    merchantId: (processor.options as any)?.descriptor?.merchantId || '',
  });

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setValidationError(t('nameRequired'));
      return;
    }
    if (!formData.securityKey.trim()) {
      setValidationError(t('routesense.credentials.securityKeyRequired'));
      return;
    }

    setValidationError(null);

    // Ensure baseCurrency is always part of supportedCurrencies.
    const supportedCurrencies = formData.supportedCurrencies.includes(formData.baseCurrency)
      ? formData.supportedCurrencies
      : [...formData.supportedCurrencies, formData.baseCurrency];

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'routesense',
          name: formData.name,
          enabled: formData.enabled,
          baseCurrency: formData.baseCurrency,
          supportedCurrencies,
          monthlyProcessingCapacity: moneyStringOrNumberToMinorUnits(
            formData.monthlyProcessingCapacity,
            formData.baseCurrency,
          ),
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            securityKey: formData.securityKey,
            descriptor: {
              descriptor: formData.descriptor,
              mcc: formData.mcc,
              merchantId: formData.merchantId,
            },
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('routesense.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Currency Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('routesense.currency.currencySettingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="supportedCurrencies">{t('routesense.currency.supportedCurrenciesLabel')}</Label>
              <MultiSelect
                options={CURRENCY_OPTIONS}
                selected={formData.supportedCurrencies}
                onChange={(value) => setFormData({ ...formData, supportedCurrencies: value })}
                placeholder={t('routesense.currency.searchForCurrency')}
              />
            </div>
            <div>
              <Label htmlFor="baseCurrency">{t('routesense.currency.baseCurrencyLabel')}</Label>
              <Select
                value={formData.baseCurrency}
                onValueChange={(value) => setFormData({ ...formData, baseCurrency: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('routesense.currency.selectBaseCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_CURRENCIES.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="monthlyProcessingCapacity">{t('routesense.currency.monthlyProcessingCapacityLabel')}</Label>
              <Input
                id="monthlyProcessingCapacity"
                type="number"
                min={0}
                value={formData.monthlyProcessingCapacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthlyProcessingCapacity: Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('routesense.currency.monthlyProcessingCapacityDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">{t('routesense.testMode.description')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* RouteSense Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('routesense.credentials.securityKeyLabel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SecretInput
              label={t('routesense.credentials.securityKeyLabel')}
              value={formData.securityKey}
              onChange={(value) => setFormData({ ...formData, securityKey: value })}
              placeholder={t('routesense.credentials.securityKeyPlaceholder')}
            />
            <p className="text-muted-foreground text-xs">{t('routesense.credentials.securityKeyDescription')}</p>
          </CardContent>
        </Card>

        {/* Descriptor Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('routesense.descriptor.descriptorSettingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="descriptor">{t('routesense.descriptor.descriptorLabel')}</Label>
              <Input
                id="descriptor"
                value={formData.descriptor}
                onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
                placeholder={t('routesense.descriptor.descriptorPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('routesense.descriptor.descriptorDescription')}</p>
            </div>
            <div>
              <Label htmlFor="mcc">{t('routesense.descriptor.mccLabel')}</Label>
              <Input
                id="mcc"
                value={formData.mcc}
                onChange={(e) => setFormData({ ...formData, mcc: e.target.value })}
                placeholder={t('routesense.descriptor.mccPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('routesense.descriptor.mccDescription')}</p>
            </div>
            <div>
              <Label htmlFor="merchantId">{t('routesense.descriptor.merchantIdLabel')}</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder={t('routesense.descriptor.merchantIdPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('routesense.descriptor.merchantIdDescription')}</p>
            </div>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  );
};

export const RouteSenseEditPageContainer = withRequiredPermission(
  RouteSenseEditPageContainerInner,
  PERMISSIONS.PROCESSORS_MANAGE,
);
